import React, { useEffect, useState } from 'react';
import { Typography, Breadcrumb, Card, Form, Input, Select, Button, Space, Table, Tag, message } from 'antd';
import api from '../../../utils/api';
import { useAuth } from '../../../contexts/AuthContext';

const { Title, Paragraph } = Typography;
const { TextArea } = Input;

const SupportCenter = () => {
  const [form] = Form.useForm();
  const { currentUser } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [tickets, setTickets] = useState([]);

  const columns = [
    { title: 'Ticket #', dataIndex: 'id', key: 'id' },
    { title: 'Subject', dataIndex: 'subject', key: 'subject' },
    { title: 'Category', dataIndex: 'category', key: 'category' },
    { title: 'Priority', dataIndex: 'priority', key: 'priority', render: p => <Tag color={p === 'high' ? 'red' : p === 'medium' ? 'orange' : 'blue'}>{p}</Tag> },
    { title: 'Status', dataIndex: 'status', key: 'status', render: s => <Tag color={s === 'resolved' ? 'green' : s === 'open' ? 'blue' : 'default'}>{s}</Tag> },
    { title: 'Created', dataIndex: 'created_at', key: 'created_at' },
  ];

  useEffect(() => {
    let alive = true;
    async function load() {
      if (!currentUser) return;
      try {
        setLoading(true);
        const qp = currentUser.id ? `requester_id=${currentUser.id}` : (currentUser.email ? `requester_email=${encodeURIComponent(currentUser.email)}` : '');
        const data = await api.get(`/support-tickets?${qp}&limit=100`);
        const items = Array.isArray(data?.items) ? data.items : [];
        if (alive) setTickets(items);
      } catch (e) {
        console.error(e);
        message.error('Failed to load your tickets');
      } finally {
        if (alive) setLoading(false);
      }
    }
    load();
    return () => { alive = false; };
  }, [currentUser]);

  const onSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);
      const payload = {
        subject: values.subject,
        description: values.description,
        category: values.category,
        priority: String(values.priority || 'Medium').toLowerCase(),
        requester_email: currentUser?.email || null,
        requester_id: currentUser?.id || null,
      };
      const res = await api.post('/support-tickets', payload);
      // Optimistically reload list
      const qp = currentUser?.id ? `requester_id=${currentUser.id}` : (currentUser?.email ? `requester_email=${encodeURIComponent(currentUser.email)}` : '');
      const data = await api.get(`/support-tickets?${qp}&limit=100`);
      const items = Array.isArray(data?.items) ? data.items : [];
      setTickets(items);
      form.resetFields();
      message.success(`Support ticket submitted${res?.id ? ` (#${res.id})` : ''}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <Breadcrumb items={[{ title: 'Client' }, { title: 'Support' }]} />

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 12, marginBottom: 16 }}>
        <Title level={3} style={{ margin: 0 }}>Support Center</Title>
        <Space>
          <Button type="primary" onClick={onSubmit} loading={submitting}>Submit Ticket</Button>
        </Space>
      </div>

      <Card style={{ marginBottom: 16 }}>
        <Paragraph type="secondary" style={{ marginBottom: 12 }}>Raise a ticket and our team will respond promptly.</Paragraph>
        <Form layout="vertical" form={form}>
          <Form.Item label="Subject" name="subject" rules={[{ required: true, message: 'Please enter a subject' }]}>
            <Input placeholder="Brief issue summary" />
          </Form.Item>
          <Form.Item label="Category" name="category" rules={[{ required: true }]}>
            <Select placeholder="Select a category">
              <Select.Option value="Campaigns">Campaigns</Select.Option>
              <Select.Option value="Creatives">Creatives</Select.Option>
              <Select.Option value="Audiences">Audiences</Select.Option>
              <Select.Option value="Integrations">Integrations</Select.Option>
              <Select.Option value="Billing">Billing</Select.Option>
              <Select.Option value="Other">Other</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item label="Priority" name="priority" initialValue="Medium" rules={[{ required: true }]}>
            <Select>
              <Select.Option value="Low">Low</Select.Option>
              <Select.Option value="Medium">Medium</Select.Option>
              <Select.Option value="High">High</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item label="Description" name="description" rules={[{ required: true, message: 'Please describe the issue' }]}>
            <TextArea rows={5} placeholder="Provide details, steps to reproduce, screenshots/links if applicable" />
          </Form.Item>
        </Form>
      </Card>

      <Card>
        <Paragraph type="secondary" style={{ marginBottom: 12 }}>Your recent tickets</Paragraph>
        <Table rowKey="id" columns={columns} dataSource={tickets} loading={loading} pagination={{ pageSize: 8 }} />
      </Card>
    </div>
  );
};

export default SupportCenter;
